import React, { FC, useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import { FormProvider, useForm } from "react-hook-form";
import { Alert, Button, Col, Row } from "reactstrap";
import Swal from "sweetalert2";
import { ReactTyped } from "react-typed";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import { machineryApis } from "../../../api/MachineryNorms";
import { documentApis } from "../../../api/Documents";
import adminOrgApis from "../../../api/master/adminOrg";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { MACHINERY_HEADS, DEFAULT_MACHINERY_HEAD } from "./machineryHeads";
import { useWbsName } from "../shared/useWbsCodes";

// Add/edit ONE flat machinery-norm row (Activity, Norm Code, Description,
// Nearest Dump, Total Lead, Rate, UOM). The rate depends on Activity + Norm
// Code; the unique key is Activity Name + Norm Code + Description.
//
// Activity field is scope-dependent:
//   PROJECT scope (projectId > 0) → required Select over the project's
//     activities (reuses /activity/dropdown via documentApis); picking one sets
//     BOTH activityId (resolved FK) and activityName (label).
//   ORG scope (projectId 0)      → required free-text Activity Name; no
//     activityId is sent (org-master rows are unlinked by design).
// Edge case: editing a PROJECT-scope row whose activityId is null (e.g. an
// upload row whose text didn't match any activity) falls back to the free-text
// input so the row stays editable without forcing a re-link.

type MachineryNormForm = {
  activityId?: number;
  activityName: string;
  normCode: string;
  description?: string;
  nearestDump?: string;
  totalLead?: number;
  rate?: number;
  uomId?: number;
};

const defaultForm: MachineryNormForm = {
  activityId: undefined,
  activityName: "",
  normCode: "",
  description: "",
  nearestDump: "",
  totalLead: undefined,
  rate: undefined,
  uomId: undefined,
};

const toNumberOrUndefined = (value: any): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const MachineryNormModal: FC<{
  open: boolean;
  /** machineryNormId when editing, null/undefined when creating. */
  id?: number | null;
  /** Scope for a NEW row — sent on create only (immutable afterwards). */
  category?: string;
  projectId?: number;
  onClose: () => void;
  onSuccess: () => void;
}> = ({
  open,
  id,
  category = DEFAULT_MACHINERY_HEAD,
  projectId = 0,
  onClose,
  onSuccess,
}) => {
  const wbsName = useWbsName();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uomOptions, setUomOptions] = useState<SelectOption[]>([]);
  const [activityOptions, setActivityOptions] = useState<SelectOption[]>([]);
  // Project-scope row loaded with activityId null → keep the free-text field.
  const [unlinkedEdit, setUnlinkedEdit] = useState(false);
  const [warning, setWarning] = useState<{
    error?: string;
    message?: string;
  }>({});

  const methods = useForm<MachineryNormForm>({
    mode: "onChange",
    defaultValues: { ...defaultForm },
  });
  const { handleSubmit, reset, setValue, getValues } = methods;

  const isEdit = !!id;
  const isProjectScope = projectId > 0;
  const useActivitySelect = isProjectScope && !unlinkedEdit;
  const resetWarning = () => setWarning({});

  const fetchUomOptions = async () => {
    try {
      const response = await adminOrgApis.fetchUom();
      const { data, status } = response || {};
      if (status && Array.isArray(data)) {
        const options = data.map((item: any) => ({
          value: item?.workUomId ?? item?.uomId ?? item?.id,
          label: item?.uomShortCode
            ? `${item?.uomName} (${item?.uomShortCode})`
            : item?.uomName,
        }));
        setUomOptions(options);
        // Mucking rates are per cubic meter — default a NEW row to 'Cum'.
        if (!id && getValues("uomId") === undefined) {
          const cum = options.find((opt: SelectOption) =>
            /\bcum\b/i.test(String(opt.label || ""))
          );
          if (cum) setValue("uomId", Number(cum.value));
        }
      }
    } catch (error) {
      console.error("Error fetching UOMs:", error);
    }
  };

  // Project's activities for the Activity select (project scope only). Reuses
  // the same lightweight /activity/dropdown the Documents page uses.
  const fetchActivityOptions = async () => {
    try {
      const response = await documentApis.fetchActivityDropDown({ projectId });
      const list = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : [];
      setActivityOptions(
        list.map((activity: any) => ({
          value: Number(activity?.activityId),
          label: wbsName(activity?.activityName),
        }))
      );
    } catch (error) {
      console.error("Error fetching project activities:", error);
      setActivityOptions([]);
    }
  };

  const fetchById = async (machineryNormId: number) => {
    try {
      setLoading(true);
      const apiResponse: ApiResponse = await machineryApis.getById(
        machineryNormId
      );
      const { data, status } = apiResponse;
      if (status && data) {
        const loadedActivityId = toNumberOrUndefined(data?.activityId);
        setUnlinkedEdit(isProjectScope && !loadedActivityId);
        setValue("activityId", loadedActivityId);
        setValue("activityName", data?.activityName ?? "");
        setValue("normCode", data?.normCode ?? "");
        setValue("description", data?.description ?? "");
        setValue("nearestDump", data?.nearestDump ?? "");
        // decimals arrive as strings from the API — parse safely
        setValue("totalLead", toNumberOrUndefined(data?.totalLead));
        setValue("rate", toNumberOrUndefined(data?.rate));
        setValue("uomId", toNumberOrUndefined(data?.uomId));
      }
    } catch (error) {
      console.error("Error loading machinery norm:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    resetWarning();
    setUnlinkedEdit(false);
    reset({ ...defaultForm });
    fetchUomOptions();
    if (projectId > 0) {
      fetchActivityOptions();
    } else {
      setActivityOptions([]);
    }
    if (id) {
      fetchById(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id, projectId]);

  const buildPayload = (form: MachineryNormForm) => {
    const payload: any = {
      // activityId only when actually linked (project scope + a selection);
      // org-master rows carry the free text alone.
      activityId:
        useActivitySelect && form.activityId
          ? Number(form.activityId)
          : undefined,
      activityName: form.activityName?.trim(),
      normCode: form.normCode?.trim(),
      description: form.description?.trim() || undefined,
      nearestDump: form.nearestDump?.trim() || undefined,
      totalLead: toNumberOrUndefined(form.totalLead),
      rate: toNumberOrUndefined(form.rate),
      uomId: toNumberOrUndefined(form.uomId),
    };
    return payload;
  };

  const onSubmit = async (form: MachineryNormForm) => {
    try {
      resetWarning();
      setSubmitting(true);
      const payload = buildPayload(form);
      const response: ApiResponse = isEdit
        ? await machineryApis.update({
            machineryNormId: id as number,
            ...payload,
          })
        : await machineryApis.create({ ...payload, category, projectId });
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: isEdit ? "Updated Successfully" : "Created Successfully",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        onSuccess();
        onClose();
      } else {
        setWarning({ message, error });
      }
    } catch (error) {
      console.error("Error saving machinery norm:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const categoryLabel =
    MACHINERY_HEADS.find((head) => head.key === category)?.label || category;

  return (
    <Modal
      maskClosable={false}
      title={
        isEdit
          ? `Edit Machinery Norm (${categoryLabel})`
          : `Add Machinery Norm (${categoryLabel})`
      }
      open={open}
      footer={null}
      width={720}
      onCancel={onClose}
      destroyOnClose
    >
      {loading ? (
        <div className="text-center py-5">
          <Spin size="large" />
        </div>
      ) : (
        <FormProvider {...methods}>
          <form>
            <Alert
              isOpen={warning?.message !== undefined}
              color="danger"
              className="start-0 w-100 py-1 mt-1 mb-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <span className="fw-bold">{warning?.error}: </span>
              <ReactTyped
                strings={[warning?.message || ""]}
                typeSpeed={10}
                showCursor={false}
              />
            </Alert>
            <div className="p-1">
              <Row className="w-100 mt-1">
                <Col lg="6">
                  {useActivitySelect ? (
                    <FormSelect
                      name="activityId"
                      label="Activity"
                      placeholder="Select project activity"
                      required
                      options={activityOptions}
                      onChange={(value: any, option: any) => {
                        // keep the stored text in lock-step with the link
                        const picked =
                          option?.label ??
                          activityOptions.find(
                            (opt) => Number(opt.value) === Number(value)
                          )?.label ??
                          "";
                        setValue("activityName", String(picked), {
                          shouldValidate: true,
                        });
                      }}
                    />
                  ) : (
                    <>
                      <FormInput
                        name="activityName"
                        label="Activity Name"
                        placeholder="e.g. ADIT-3A EXCAVATION(AE-0102)"
                        type="text"
                        required
                        maxLength={255}
                      />
                      {unlinkedEdit && (
                        <div
                          style={{
                            color: "#f4bd0e",
                            fontSize: 12,
                            marginTop: 2,
                          }}
                        >
                          Not linked to a project activity — stored as text.
                        </div>
                      )}
                    </>
                  )}
                </Col>
                <Col lg="6">
                  <FormInput
                    name="normCode"
                    label="Norm Code"
                    placeholder="e.g. B3.1.1"
                    type="text"
                    required
                    maxLength={100}
                  />
                </Col>
              </Row>
              <Row className="w-100 mt-2">
                <Col lg="6">
                  <FormInput
                    name="description"
                    label="Description"
                    placeholder="e.g. Class III"
                    type="text"
                    maxLength={500}
                  />
                </Col>
                <Col lg="6">
                  <FormInput
                    name="nearestDump"
                    label="Nearest Dump"
                    placeholder="e.g. Hiller Peernia"
                    type="text"
                    maxLength={255}
                  />
                </Col>
              </Row>
              <Row className="w-100 mt-2">
                <Col lg="4">
                  <FormInput
                    name="totalLead"
                    label="Total Lead"
                    type="number"
                    pattern={/^\d+(\.\d{1,5})?$/}
                    min={0}
                    placeholder="e.g. 2.50038"
                    customValidation={{
                      validate: (value: any) =>
                        value === undefined ||
                        value === null ||
                        value === "" ||
                        /^\d+(\.\d{1,5})?$/.test(String(value)) ||
                        "Total lead allows max 5 decimals",
                    }}
                  />
                </Col>
                <Col lg="4">
                  <FormInput
                    name="rate"
                    label="Rate (₹)"
                    type="number"
                    pattern={/^\d+(\.\d{1,4})?$/}
                    min={0}
                    placeholder="Rate per unit"
                    customValidation={{
                      validate: (value: any) =>
                        value === undefined ||
                        value === null ||
                        value === "" ||
                        /^\d+(\.\d{1,4})?$/.test(String(value)) ||
                        "Rate allows max 4 decimals",
                    }}
                  />
                </Col>
                <Col lg="4">
                  <FormSelect
                    name="uomId"
                    label="UOM"
                    placeholder="Select unit"
                    options={uomOptions}
                  />
                </Col>
              </Row>
            </div>
            <Row className="mt-4">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      className="btn btn-primary btn-sm"
                      type="button"
                      color="primary"
                      disabled={submitting}
                      onClick={handleSubmit(onSubmit)}
                    >
                      {isEdit ? "Update" : "Save"}
                    </Button>
                  </li>
                  <li>
                    <a
                      href="#cancel"
                      onClick={(ev) => {
                        ev.preventDefault();
                        onClose();
                      }}
                      className="link link-light"
                    >
                      Cancel
                    </a>
                  </li>
                </ul>
              </Col>
            </Row>
          </form>
        </FormProvider>
      )}
    </Modal>
  );
};

export default MachineryNormModal;
